import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import PageName from "../../components/PageName";
import AccountCard from "../../components/AccountCard";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getDashboardBalances } from "../../redux/selfService/selfServiceSlice";
import CopyToClipboard from "../../components/CopyComponent/copy-to-clipboard";
import LoadingSpinner from "../../components/LoadingSpinner";
import InactivityTimeoutHOC from "../auth/inactivity-timeout-hoc";
import TimeOutActivityLayout from "../auth/inactivity-timeout-v2";
import { RouteNames } from "../../constants";

/** Live API may already return CPS/PPP blocks; local may split PPP into vc* fields. */
const toSchemeValue = (cpsOrCombined?: string, ppp?: string) => {
  const main = (cpsOrCombined ?? "").trim();
  if (/\bCPS\b/i.test(main) && /\bPPP\b/i.test(main)) {
    return main;
  }
  const cpsAmount = main || "0.00";
  const pppAmount = (ppp ?? "").trim() || "0.00";
  return `CPS\n${cpsAmount}\nPPP\n${pppAmount}`;
};

const Dashboard = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [hideBalances, setHideBalances] = useState(false);
  const { isDashboardLoading, dashboardBalances } = useAppSelector(
    (state) => state?.selfService
  );
  const dispatch = useAppDispatch();
  const isRecaptured = !!user?.isRecaptured;
  const isRegistrationIncomplete = !!user?.isRegistrationIncomplete;

  useEffect(() => {
    const hideBalance = localStorage.getItem("hide-dashboard-balances");
    if (hideBalance) {
      setHideBalances(parseInt(hideBalance) == 1);
    }
  }, []);

  useEffect(() => {
    console.log("user details", user);
    if (user && user.pin && !isRegistrationIncomplete) {
      // @ts-ignore
      dispatch(getDashboardBalances(user.pin));
    }
  }, [user, isRegistrationIncomplete]);

  const cards = useMemo(() => {
    if (!dashboardBalances) return [];
    return [
      {
        headText: "Current Value",
        mainValue: toSchemeValue(
          dashboardBalances.currentValue,
          dashboardBalances.vcCurrentValue
        ),
      },
      {
        headText: "Total Contribution",
        mainValue: toSchemeValue(
          dashboardBalances.totalContribution,
          dashboardBalances.vcTotalContribution
        ),
      },
      {
        headText: "Growth",
        mainValue: toSchemeValue(
          dashboardBalances.growth,
          dashboardBalances.vcGrowth
        ),
      },
      {
        headText: "Net Contribution",
        mainValue: toSchemeValue(
          dashboardBalances.netContribution,
          dashboardBalances.vcNetContribution
        ),
      },
      {
        headText: "Unit Price",
        mainValue: toSchemeValue(
          dashboardBalances.unitPrice,
          dashboardBalances.vcUnitPrice
        ),
      },
      {
        headText: "Total Units",
        mainValue: toSchemeValue(
          dashboardBalances.totalUnits,
          dashboardBalances.vcTotalUnits
        ),
      },
    ];
  }, [dashboardBalances]);

  return (
    <TimeOutActivityLayout>
    <div>
      <PageName pageName={"Home"} />
      {isRegistrationIncomplete ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong className="block mb-1">Complete your RSA registration</strong>
          Please finish Personal Data, Employer, Monthly Contribution, Next of Kin and Documents to continue.{" "}
          <Link to={RouteNames.REGISTER_COMPLETE} className="font-semibold underline text-amber-950">
            Continue registration
          </Link>
        </div>
      ) : !isRecaptured ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong className="block mb-1">Data recapture required</strong>
          Please complete your RSA data recapture to unlock other self-service menus.{" "}
          <Link to={RouteNames.RECAPTURE} className="font-semibold underline text-amber-950">
            Go to Recapture
          </Link>
        </div>
      ) : (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          <strong className="block mb-1">Recapture completed</strong>
          You have already completed data recapture. The Recapture and Recapture Status menus are no longer available.
        </div>
      )}
      <div className="dashboard-ztem">
        <div className="dashboard-user">
          <div>
            <div className="welcome-text">
              Welcome, {`${user.firstName} ${user.surname}`}
              {!isRegistrationIncomplete && user?.pin ? (
                <div className="badges">
                  <CopyToClipboard
                    text={`PIN: ${user.pin}${user.fund ? ` | ${user.fund}` : ""}`}
                  />
                </div>
              ) : null}
            </div>
          </div>
          <div>
            {!hideBalances ? (
              <button
                className="btn btn-outline btn-flat"
                onClick={() => {
                  localStorage.setItem("hide-dashboard-balances", "1");
                  setHideBalances(true);
                }}
              >
                <FiEyeOff className="mr-2" />
                Hide Balance
              </button>
            ) : (
              <button
                className="btn btn-outline btn-flat"
                onClick={() => {
                  localStorage.setItem("hide-dashboard-balances", "0");
                  setHideBalances(false);
                }}
              >
                <FiEye className="mr-2" />
                Show Balance
              </button>
            )}
          </div>
        </div>
        {isDashboardLoading ? (
          <div
            className="dashboard-balance-loading"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          cards.length > 0 && (
            <div className="dashboard-cards">
              {cards.map((card) => (
                <AccountCard
                  key={card.headText}
                  headText={card.headText}
                  mainValue={card.mainValue}
                  hideBalance={hideBalances}
                />
              ))}
            </div>
          )
        )}
      </div>
    </div>
    </TimeOutActivityLayout>
  );
};
export default InactivityTimeoutHOC(Dashboard, 900000);
